import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MissingDeviceKeyError } from '../lib/ownerDeviceKey';
import { useAuth } from '../lib/auth/AuthProvider';
import { getMyVault } from '../lib/vault/vaultApi';
import {
  listReplies,
  decryptOwnerLetterReply,
  markReplyViewed,
  type ReplyListItem,
} from '../lib/replies/repliesApi';
import './guardians.css';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * The owner's inbox of replies — the "two-way" of the message. Recipients can
 * write back; those replies are sealed to the owner's device key and read here,
 * decrypted on-device. (Video/voice replies arrive as a follow-on; letter
 * replies are readable now.)
 */
export default function RepliesScreen() {
  const { api, user } = useAuth();
  const [vaultId, setVaultId] = useState<string | null>(null);
  const [replies, setReplies] = useState<ReplyListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsKit, setNeedsKit] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [openText, setOpenText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const dash = await getMyVault(api);
      setVaultId(dash.vault.id);
      const res = await listReplies(api, dash.vault.id);
      setReplies(res.responses);
    } catch {
      setError('We could not load your replies just now.');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const openReply = useCallback(
    async (r: ReplyListItem) => {
      if (vaultId === null || user?.email === undefined) return;
      if (r.format !== 'letter') {
        setError('That reply is a recording — reading recordings back is coming soon.');
        return;
      }
      setBusy(true);
      setError(null);
      setOpenId(r.id);
      setOpenText(null);
      try {
        const { text } = await decryptOwnerLetterReply({
          api,
          vaultId,
          responseId: r.id,
          ownerEmail: user.email,
        });
        setOpenText(text);
        // Mark viewed only AFTER a successful on-device decrypt (best-effort).
        void markReplyViewed(api, vaultId, r.id).catch(() => undefined);
        setReplies((prev) =>
          prev === null ? prev : prev.map((x) => (x.id === r.id ? { ...x, viewed_at: new Date().toISOString() } : x)),
        );
      } catch (e) {
        // "This device has no key" is a different problem from "that did not
        // work", and it has a specific fix. Collapsing the two sends someone to
        // sign in again, which will not help however many times they try.
        setNeedsKit(e instanceof MissingDeviceKeyError);
        setError(e instanceof MissingDeviceKeyError
          ? e.message
          : 'We could not open that reply on this device.');
        setOpenId(null);
      } finally {
        setBusy(false);
      }
    },
    [api, vaultId, user?.email],
  );

  const list = replies ?? [];

  return (
    <section className="guard">
      <p className="guard__kicker">What came back</p>
      <h1 className="guard__title">Replies.</h1>
      <p className="guard__sub">
        When someone opens a message you left them, they can write back. Their words are sealed to only
        you — opened here, on your device.
      </p>

      {needsKit && (
        <p className="guard__sub">
          <Link className="la-btn-secondary" to="/recover/kit">Enter my Recovery Kit</Link>
        </p>
      )}
      {error !== null && (
        <p role="alert" className="guard__alert">
          {error}
        </p>
      )}

      {replies !== null && list.length === 0 && (
        <div className="guard__card guard__empty">
          <h2 className="guard__cardTitle">No replies yet.</h2>
          <p className="guard__body">When a message is opened and answered, it will appear here.</p>
        </div>
      )}

      {list.length > 0 && (
        <ul className="guard__list">
          {list.map((r) => (
            <li key={r.id} className="guard__card guard__item">
              <div className="guard__itemHead">
                <div>
                  <h3 className="guard__name">{r.recipient_full_name}</h3>
                  <p className="guard__meta">
                    Wrote back {formatDate(r.sealed_at)}
                    {r.format !== 'letter' ? ` · ${r.format}` : ''}
                  </p>
                </div>
                {r.viewed_at === null && <span className="guard__pill guard__pill--active">New</span>}
              </div>

              {openId === r.id && openText !== null ? (
                <p className="guard__body" style={{ whiteSpace: 'pre-wrap', marginTop: '12px' }}>
                  {openText}
                </p>
              ) : (
                <div className="guard__actions">
                  <button
                    type="button"
                    className="la-btn-secondary"
                    disabled={busy}
                    onClick={() => void openReply(r)}
                  >
                    {busy && openId === r.id ? 'Opening…' : 'Read it'}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
